/**
 * Hub frontend — fetch wrapper.
 *
 * Hub auth is intentionally separate from the app auth flow:
 * - Token is stored in localStorage.ml_hub_token (NOT ml_token)
 * - JWT carries aud="hub" — different signature claim than app JWTs
 * - Hub endpoints all live under /hub/* on hub.usetenon.com (the hub mux)
 *   which 404s /api/* paths by design
 *
 * Cross-origin to /api/v1/hub/errors (which is on the APP mux, not the
 * hub mux) is a deliberate exception — the existing /god-view error feed
 * already lives there and HUB-9-1 didn't pull it onto the hub mux.
 */

const HUB_TOKEN_KEY = 'ml_hub_token'
const APP_ORIGIN = 'https://app.usetenon.com'

export function getHubToken(): string | null {
  try {
    return localStorage.getItem(HUB_TOKEN_KEY)
  } catch {
    return null
  }
}

export function setHubToken(token: string) {
  localStorage.setItem(HUB_TOKEN_KEY, token)
}

export function clearHubToken() {
  localStorage.removeItem(HUB_TOKEN_KEY)
}

async function request<T>(path: string, init: RequestInit = {}, opts: { crossOriginToApp?: boolean } = {}): Promise<T> {
  const token = getHubToken()
  const headers = new Headers(init.headers)
  if (!headers.has('Content-Type') && init.body) headers.set('Content-Type', 'application/json')
  if (token) headers.set('Authorization', `Bearer ${token}`)

  const url = opts.crossOriginToApp ? `${APP_ORIGIN}${path}` : path
  const res = await fetch(url, {
    ...init,
    headers,
    credentials: opts.crossOriginToApp ? 'omit' : 'same-origin',
  })
  if (res.status === 401) {
    clearHubToken()
    if (typeof window !== 'undefined' && window.location.pathname !== '/auth/login') {
      window.location.href = '/auth/login'
    }
    throw new Error('Unauthorized')
  }
  if (!res.ok) {
    let msg = `HTTP ${res.status}`
    try {
      const body = await res.json()
      if (body?.error) msg = body.error
    } catch {}
    throw new Error(msg)
  }
  if (res.status === 204) return undefined as T
  return res.json() as Promise<T>
}

// ── Auth ─────────────────────────────────────────────────────────────────────

export async function hubLogin(email: string, password: string): Promise<{ token: string; admin: { id: number; email: string; name: string } }> {
  const res = await fetch('/hub/auth/login', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, password }),
  })
  if (!res.ok) {
    let msg = 'Invalid credentials'
    try {
      const body = await res.json()
      if (body?.error) msg = body.error
    } catch {}
    throw new Error(msg)
  }
  return res.json()
}

export async function hubMe(): Promise<{ id: number; email: string; name: string }> {
  return request('/hub/me')
}

// ── Queue ────────────────────────────────────────────────────────────────────

export interface HubQueueItem {
  id: string
  row_id: string | null
  type: string
  title: string
  description: string | null
  status: string
  priority: string
  origin: string | null
  gate: string | null
  safety: string | null
  sort_position: number | null
  wip_session_id: string | null
  claimed_at: string | null
  shipped_at: string | null
  merged_commit_sha: string | null
  github_pr_number: number | null
  github_pr_state: string | null
  metadata?: any
  created_at: string
  updated_at: string
  model_hint?: 'sonnet' | 'opus' | 'haiku' | null
  deployed_at: string | null
  deployed_commit_sha: string | null
  deployed_environment: string | null
  last_heartbeat_at?: string | null
}

export interface HubQueueListResponse {
  items: HubQueueItem[]
  total: number
}

export interface HubQueueActivityRow {
  id: number
  workflow_item_id: string | null
  event_type: string
  actor: string | null
  payload?: any
  created_at: string
}

export interface HubPlatformPulse {
  bundle_hash: string
  migration_version: number
  uptime_seconds: number
  process_started_at: string
  app_env: string
  last_check: string
}

export interface HubStats {
  shipped_today: number
  shipped_yesterday: number
  open_p0: number
  active_claims: number
  errors_today_open: number
  errors_last_hour: number
  last_ship_sha?: string
  last_ship_at?: string
  last_ship_row_id?: string
  pulse?: HubPlatformPulse
  ships_history?: number[]
}

export const hubQueue = {
  list: (params: Record<string, string | number | undefined> = {}) => {
    const usp = new URLSearchParams()
    for (const [k, v] of Object.entries(params)) {
      if (v !== undefined && v !== null && v !== '') usp.set(k, String(v))
    }
    const qs = usp.toString()
    return request<HubQueueListResponse>(`/hub/queue${qs ? '?' + qs : ''}`)
  },
  get: (rowId: string) => request<HubQueueItem>(`/hub/queue/${encodeURIComponent(rowId)}`),
  create: (body: Partial<HubQueueItem>) =>
    request<HubQueueItem>('/hub/queue', { method: 'POST', body: JSON.stringify(body) }),
  update: (rowId: string, body: Partial<HubQueueItem>) =>
    request<HubQueueItem>(`/hub/queue/${encodeURIComponent(rowId)}`, { method: 'PATCH', body: JSON.stringify(body) }),
  claim: (rowId: string, wipSessionId: string) =>
    request<HubQueueItem>(`/hub/queue/${encodeURIComponent(rowId)}/claim`, {
      method: 'POST',
      body: JSON.stringify({ wip_session_id: wipSessionId }),
    }),
  release: (rowId: string, wipSessionId?: string, note?: string) =>
    request<HubQueueItem>(`/hub/queue/${encodeURIComponent(rowId)}/release`, {
      method: 'POST',
      body: JSON.stringify({ wip_session_id: wipSessionId, note }),
    }),
  done: (rowId: string, opts: { commit_sha?: string; status_reason?: string; note?: string } = {}) =>
    request<HubQueueItem>(`/hub/queue/${encodeURIComponent(rowId)}/done`, {
      method: 'POST',
      body: JSON.stringify(opts),
    }),
  activity: (rowId: string, limit = 100) =>
    request<{ activity: HubQueueActivityRow[]; total: number; item_id: string; row_id: string | null }>(
      `/hub/queue/${encodeURIComponent(rowId)}/activity?limit=${limit}`
    ),
  heartbeat: (rowId: string) =>
    request<{ ok: boolean; row_id: string; heartbeat_at: string }>(
      `/hub/queue/${encodeURIComponent(rowId)}/heartbeat`,
      { method: 'POST', body: '{}' }
    ),
}

export const hubStats = () => request<HubStats>('/hub/stats')

// ── Cass ship-reviewer monitor ───────────────────────────────────────────────
export interface CassSessionSummary {
  session_id: string
  status: string
  started_at: string
  ended_at?: string | null
  duration_seconds?: number | null
  passed: number
  partial: number
  phantom: number
  skipped: number
  reviewed_total: number
}

export interface CassVerdictRow {
  row_id: string
  verdict: string
  evidence: string
  reviewed_at: string
  session_id?: string
}

export interface CassStatus {
  interval_seconds: number
  next_eta_seconds: number
  pending_count: number
  /** "github-live" | "unavailable" — when unavailable, render "—", never 0 (#1375) */
  pending_source?: string
  last_session?: CassSessionSummary | null
  recent_sessions: CassSessionSummary[]
  recent_verdicts: CassVerdictRow[]
}

export const hubCassStatus = () => request<CassStatus>('/hub/cass/status')

export interface HubGlobalActivityRow extends HubQueueActivityRow {
  row_id: string | null
  item_title: string | null
}

export const hubGlobalActivity = (limit = 50) =>
  request<{ activity: HubGlobalActivityRow[]; total: number }>(
    `/hub/activity/recent?limit=${limit}`
  )

// ── Webhooks (HUB-12-1) ───────────────────────────────────────────────

export interface HubWebhookEvent {
  id: string
  source: 'github' | 'sentry' | 'stripe'
  event_type: string
  signature_valid: boolean | null
  status_code: number
  matched_row_id: string
  error_message: string
  received_at: string
  payload?: any
}

export const hubWebhooks = {
  list: (params: { source?: string; limit?: number } = {}) => {
    const usp = new URLSearchParams()
    if (params.source) usp.set('source', params.source)
    if (params.limit) usp.set('limit', String(params.limit))
    const qs = usp.toString()
    return request<{ events: HubWebhookEvent[]; total: number }>(
      `/hub/webhooks${qs ? '?' + qs : ''}`
    )
  },
  get: (id: string) =>
    request<HubWebhookEvent>(`/hub/webhooks/${encodeURIComponent(id)}`),
}

// ── Backups (HUB-12-11) — DO managed-DB snapshot visibility ─────────────

export interface HubBackup {
  cluster_id: string
  cluster_name: string
  taken_at: string
  size_gigabytes: number
}

export interface HubBackupsResponse {
  backups: HubBackup[]
  source: 'do' | 'unavailable'
  note?: string
}

export const hubBackups = () =>
  request<HubBackupsResponse>('/hub/backups')

// ── Errors (cross-origin to app mux — the existing /api/v1/hub/errors live there) ─

export interface HubErrorEvent {
  id: number
  company_id: number | null
  user_id: number | null
  endpoint: string
  method: string
  error_code: number
  error_message: string
  request_id: string | null
  severity: string
  status: string
  created_at: string
  resolved_at: string | null
  acked_at: string | null
  user_email: string
  company_name: string
}

// ── Tenants (HUB-9-7b — mirror of /api/v1/super/god-view onto hub mux) ────

export interface HubTenantSummary {
  company_id: number
  company_name: string
  plan: string | null
  subscription_status: string
  trial_ends_at: string | null
  current_period_end: string | null
  last_seen_at: string | null
  project_count: number
  user_count: number
  ai_tokens_30d: number
  flags: string[]
  mrr_cents: number
  is_sandbox: boolean
}

export interface HubTenantsResponse {
  summary: {
    total: number
    active: number
    trialing: number
    past_due: number
    cancelled: number
    mrr_cents: number
    at_risk_count: number
  }
  tenants: HubTenantSummary[]
}

export interface HubTenantDetail {
  company: {
    id: number
    name: string
    plan: string | null
    subscription_status: string
    trial_ends_at: string | null
    current_period_end: string | null
    created_at: string
    is_active: boolean
    deleted_at: string | null
    scheduled_purge_at: string | null
    is_sandbox: boolean
  }
  users: {
    id: number
    name: string
    email: string
    role: string
    last_login_at: string | null
  }[]
  subscription?: {
    stripe_customer_id: string | null
    stripe_subscription_id: string | null
    trial_ends_at: string | null
    current_period_end: string | null
    status: string
    stripe_credit_balance?: number
  }
  billing_history?: { invoices: any[]; note?: string }
  usage?: {
    users_logged_in_7d: number
    users_logged_in_30d: number
    projects_created_30d: number
    ai_tokens_daily_30d: { date: string; tokens: number }[]
  }
  flags?: { kind: string; explanation: string; raised_at: string }[]
  flag_catalog?: Record<string, string>
}

export interface HubTenantAuditEntry {
  id: number
  company_id: number
  action: string
  initiated_by: string
  severity: string
  metadata: any
  created_at: string
}

export interface HubFeatureFlagDescriptor {
  key: string
  label: string
  description: string
}

export interface HubTenantFeatureFlags {
  company_id: number
  flags: Record<string, any>
  catalog: HubFeatureFlagDescriptor[]
}

export interface HubTenantSageCostPoint {
  date: string
  cost_cents: number
  tokens: number
}

export interface HubTenantSageTopUser {
  user_id: number
  name: string
  email: string
  tokens: number
  cost_cents: number
}

export interface HubTenantSageFeatureBreakdown {
  feature: string
  calls: number
  tokens: number
  cost_cents: number
}

export interface HubTenantSageUsage {
  window_days: 30 | 90
  as_of: string
  mtd_cost_cents: number
  mtd_tokens: number
  mtd_calls: number
  projected_cost_cents: number
  monthly_budget_cents: number | null
  alert_thresholds: number[]
  day_of_month: number
  days_in_month: number
  trend: HubTenantSageCostPoint[]
  top_users: HubTenantSageTopUser[]
  feature_breakdown: HubTenantSageFeatureBreakdown[]
}

// HUB-12-10 — outbound delivery log per tenant.
export interface HubTenantOutboundLogRow {
  id: string
  channel: string
  event_kind: string
  recipient: string
  subject: string
  status: string
  status_code: number
  error_message: string
  payload_summary: string
  created_at: string
}

export interface HubTenantOutboundLogSummary {
  channel: string
  status: string
  count: number
}

export interface HubTenantOutboundLog {
  items: HubTenantOutboundLogRow[]
  summary_7d: HubTenantOutboundLogSummary[]
  limit: number
  company_id: number
}

export const hubTenants = {
  list: () => request<HubTenantsResponse>('/hub/tenants'),
  get: (id: number | string) => request<HubTenantDetail>(`/hub/tenants/${id}`),
  sageUsage: (id: number | string, days: 30 | 90 = 30) =>
    request<HubTenantSageUsage>(`/hub/tenants/${id}/sage-usage?days=${days}`),
  outboundLog: (
    id: number | string,
    opts: { limit?: number; channel?: string; status?: string } = {}
  ) => {
    const qs: string[] = []
    if (opts.limit) qs.push(`limit=${encodeURIComponent(String(opts.limit))}`)
    if (opts.channel) qs.push(`channel=${encodeURIComponent(opts.channel)}`)
    if (opts.status) qs.push(`status=${encodeURIComponent(opts.status)}`)
    const q = qs.length ? `?${qs.join('&')}` : ''
    return request<HubTenantOutboundLog>(`/hub/tenants/${id}/outbound-log${q}`)
  },
  auditLog: (id: number | string, limit = 50) =>
    request<{ items: HubTenantAuditEntry[]; total: number }>(
      `/hub/tenants/${id}/audit-log?limit=${limit}`
    ),
  extendTrial: (id: number | string, days: number) =>
    request<{ ok: string }>(`/hub/tenants/${id}/extend-trial`, {
      method: 'POST',
      body: JSON.stringify({ days }),
    }),
  suspend: (id: number | string) =>
    request<{ ok: string }>(`/hub/tenants/${id}/suspend`, { method: 'POST' }),
  reactivate: (id: number | string) =>
    request<{ ok: string }>(`/hub/tenants/${id}/reactivate`, { method: 'POST' }),
  // HUB-12-6 — manual tenant ops (hard-delete with 24h grace, restore, admin-create).
  hardDelete: (id: number | string, confirm: string) =>
    request<{ ok: boolean; id: number; grace_hours: number; purge_scheduled: boolean }>(
      `/hub/tenants/${id}/hard-delete`,
      { method: 'POST', body: JSON.stringify({ confirm }) }
    ),
  restore: (id: number | string) =>
    request<{ ok: boolean; id: number }>(
      `/hub/tenants/${id}/restore`,
      { method: 'POST' }
    ),
  // HUB-12-7 — billing admin actions (refund / credit / one-off invoice).
  // All gate on STRIPE_SECRET_KEY at the server; expect 503 if Stripe-Live
  // hasn't been activated yet.
  refund: (
    id: number | string,
    body: { invoice_id: string; amount_cents?: number; reason: string }
  ) =>
    request<{
      ok: boolean
      refund_id: string
      status: string
      amount_cents: number
    }>(`/hub/tenants/${id}/refund`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  credit: (
    id: number | string,
    body: { amount_cents: number; currency?: string; reason: string }
  ) =>
    request<{
      ok: boolean
      balance_transaction_id: string
      amount_cents: number
      currency: string
    }>(`/hub/tenants/${id}/credit`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  oneOffInvoice: (
    id: number | string,
    body: {
      amount_cents: number
      currency?: string
      description: string
      reason: string
      send_now?: boolean
    }
  ) =>
    request<{
      ok: boolean
      invoice_id: string
      invoice_item_id: string
      amount_cents: number
      currency: string
      status: string
      hosted_url?: string
    }>(`/hub/tenants/${id}/one-off-invoice`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  // HUB-7 — billing email admin action.
  sendBillingEmail: (
    id: number | string,
    body: {
      template: 'trial-ending' | 'past-due' | 'dunning' | 'custom'
      subject?: string
      html_body?: string
    }
  ) =>
    request<{ sent: number; failed: number }>(`/hub/tenants/${id}/send-billing-email`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  adminCreate: (body: {
    company_name: string
    owner_email: string
    owner_name: string
    plan?: string
    temp_password?: string
  }) =>
    request<{
      ok: boolean
      company_id: number
      company_name: string
      owner_user_id: number
      owner_email: string
      temp_password: string
    }>(`/hub/tenants/admin-create`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  getFeatureFlags: (id: number | string) =>
    request<HubTenantFeatureFlags>(`/hub/tenants/${id}/feature-flags`),
  updateFeatureFlags: (id: number | string, flags: Record<string, any>) =>
    request<HubTenantFeatureFlags>(`/hub/tenants/${id}/feature-flags`, {
      method: 'PATCH',
      body: JSON.stringify({ flags }),
    }),
}

// ── Business (HUB-12-4 — SaaS analytics widgets) ───────────────────────

export interface HubBusinessPlanRow {
  plan: string
  count: number
  mrr_cents: number
}

export interface HubBusinessMetrics {
  as_of: string
  stripe_live: boolean
  note?: string
  mrr_cents: number
  arr_cents: number
  active_count: number
  trialing_count: number
  past_due_count: number
  cancelled_count: number
  new_mrr_cents: number
  expansion_mrr_cents: number
  contraction_mrr_cents: number
  churned_mrr_cents: number
  net_new_mrr_cents: number
  gross_churn_pct_monthly: number
  net_revenue_retention_pct: number
  trial_starts_mtd: number
  trial_conversions_mtd: number
  trial_conversion_rate_pct: number
  plan_breakdown: HubBusinessPlanRow[]
}

export interface HubBusinessHistoryPoint {
  date: string
  mrr_cents: number
  arr_cents: number
  active_count: number
  trialing_count: number
  past_due_count: number
  churned_count: number
}

export const hubBusiness = {
  metrics: () => request<HubBusinessMetrics>('/hub/business/metrics'),
  history: (days: 30 | 90 | 365 = 90) =>
    request<{ days: number; points: HubBusinessHistoryPoint[] }>(
      `/hub/business/history?days=${days}`
    ),
}

// HUB-REDESIGN-GAPS — endpoints required by the design handoff that did
// not exist server-side. Power tenant-detail Activity/Usage strips (P3)
// and the Settings page Hub-access + Global-flags tables (P5).

export interface HubTenantActivityPoint {
  date: string
  value: number
}

export interface HubTenantActivityResponse {
  company_id: number
  days: number
  points: HubTenantActivityPoint[]
}

export interface HubTenantUsage {
  company_id: number
  active_users_30d: number
  projects_active: number
  storage_bytes: number
  api_calls_per_day: number
}

export const hubTenantsExtra = {
  activity: (id: number | string, days = 30) =>
    request<HubTenantActivityResponse>(`/hub/tenants/${id}/activity?days=${days}`),
  usage: (id: number | string) =>
    request<HubTenantUsage>(`/hub/tenants/${id}/usage`),
}

export interface HubAdminRow {
  id: number
  email: string
  name: string
  role: string
  mfa_enrolled: boolean
  last_login_at?: string
  last_login_ip?: string
  created_at: string
}

export const hubAdmins = {
  list: () => request<{ admins: HubAdminRow[]; total: number }>('/hub/admins'),
}

export interface HubGlobalFlagRow {
  key: string
  label: string
  description: string
  companies_enabled: number
  companies_total: number
}

export const hubGlobalFlags = {
  list: () => request<{ flags: HubGlobalFlagRow[]; total: number }>('/hub/flags'),
}

export interface SupportTicketStats {
  total: number
  filed: number
  unfiled: number
}

export const hubSupportTickets = {
  stats: () => request<SupportTicketStats>('/hub/help/tickets/stats'),
}

// HUB-WATCHDOG-PHASE2 — autonomous-queue health watchdog API.
export interface HubHealthAlert {
  id: number
  rule_name: string
  dedup_key: string
  severity: 'info' | 'warn' | 'critical'
  status: 'active' | 'acked' | 'resolved'
  title: string
  details: Record<string, any>
  occurrence_count: number
  first_seen_at: string
  last_seen_at: string
  acked_at?: string | null
  acked_by?: string | null
  resolved_at?: string | null
  auto_resolved: boolean
}

export interface HubHealthRule {
  name: string
  source: 'server' | 'watcher'
  description: string
  threshold?: string
}

export const hubHealth = {
  alerts: (status: 'active' | 'acked' | 'resolved' | 'all' = 'active') =>
    request<{ alerts: HubHealthAlert[]; total: number }>(`/hub/health/alerts?status=${status}`),
  rules: () => request<{ rules: HubHealthRule[]; total: number }>('/hub/health/rules'),
  ack: (id: number) =>
    request<{ ok: true }>(`/hub/health/alerts/${id}/ack`, { method: 'POST' }),
  resolve: (id: number) =>
    request<{ ok: true }>(`/hub/health/alerts/${id}/resolve`, { method: 'POST' }),
}

export const hubFeatureFlags = {
  bulkToggle: (key: string, value: any, plan: string) =>
    request<{ key: string; plan: string; rows: number; value: any }>(
      '/hub/feature-flags/bulk',
      { method: 'POST', body: JSON.stringify({ key, value, plan }) }
    ),
}

export const hubErrors = {
  list: (params: Record<string, string | number | undefined> = {}) => {
    const usp = new URLSearchParams()
    for (const [k, v] of Object.entries(params)) {
      if (v !== undefined && v !== null && v !== '') usp.set(k, String(v))
    }
    const qs = usp.toString()
    return request<{ errors: HubErrorEvent[]; total: number }>(
      `/hub/errors${qs ? '?' + qs : ''}`
    )
  },
  get: (id: number) => request<HubErrorEvent>(`/hub/errors/${id}`),
  ack: (id: number) =>
    request<HubErrorEvent>(`/hub/errors/${id}/ack`, { method: 'POST' }),
  resolve: (id: number) =>
    request<HubErrorEvent>(`/hub/errors/${id}/resolve`, { method: 'POST' }),
  bulk: (ids: number[], action: 'ack' | 'resolve') =>
    request<{ updated: number; action: string }>('/hub/errors/bulk', {
      method: 'POST',
      body: JSON.stringify({ ids, action }),
    }),
}

export interface HubLogEntry {
  journal_ts: string
  ts?: string
  level?: string
  handler?: string
  msg?: string
  error?: string
  context?: Record<string, string>
  request_id?: string
  unit?: string
  raw?: string
}

export interface HubLogsSearchResponse {
  entries: HubLogEntry[]
  count: number
  truncated: boolean
  source: 'journalctl' | 'unavailable'
  note?: string
}

export const hubLogs = {
  search: (params: { q?: string; since?: string; level?: string; request_id?: string; limit?: number } = {}) => {
    const usp = new URLSearchParams()
    for (const [k, v] of Object.entries(params)) {
      if (v !== undefined && v !== null && v !== '') usp.set(k, String(v))
    }
    const qs = usp.toString()
    return request<HubLogsSearchResponse>(`/hub/logs/search${qs ? '?' + qs : ''}`)
  },
}

// HUB-12-9 — super_admin API key management.
export interface HubApiKey {
  id: string
  key_prefix: string
  display_name: string
  scope: string
  company_id: number
  company_name: string
  user_id: number
  user_name: string
  user_email: string
  created_at: string
  last_used_at: string | null
  expires_at: string | null
  revoked_at: string | null
}

export interface HubApiKeysResponse {
  keys: HubApiKey[]
  total: number
  note?: string
}

export const hubApiKeys = {
  list: (params: { status?: 'all' | 'active' | 'revoked'; company_id?: number } = {}) => {
    const usp = new URLSearchParams()
    if (params.status) usp.set('status', params.status)
    if (params.company_id) usp.set('company_id', String(params.company_id))
    const qs = usp.toString()
    return request<HubApiKeysResponse>(`/hub/api-keys${qs ? '?' + qs : ''}`)
  },
  revoke: (id: string, reason: string) =>
    request<{ ok: boolean; id: string; already_revoked: boolean }>(
      `/hub/api-keys/${encodeURIComponent(id)}/revoke`,
      { method: 'POST', body: JSON.stringify({ reason }) }
    ),
}

// ── Support inbox (HUB-12-12) ────────────────────────────────────────────────

export interface SupportInquiry {
  id: number
  from_email: string
  from_name: string
  to_address: string
  subject: string
  body_text?: string
  received_at: string
  status: 'open' | 'closed'
  closed_at?: string | null
  last_activity_at: string
  reply_count: number
}

export interface SupportReply {
  id: number
  admin_id: number
  admin_name: string
  admin_email: string
  body: string
  sent_at: string
}

export interface SupportListResponse {
  inquiries: SupportInquiry[]
  total: number
}

export interface SupportDetailResponse {
  inquiry: SupportInquiry
  replies: SupportReply[]
}

// ── Business Autonomy Phase 2 — agent observability ─────────────────────────
//
// Reads agent_status / agent_sessions / agent_audit_log (Phase 1 schema) plus
// per-department escalation slice from workflow_items. All endpoints behind
// platformAuth (super_admin + admin hub JWTs).

export interface HubAutonomyAgent {
  agent_id: string
  department: string
  product: string
  current_status: 'idle' | 'running' | 'paused' | 'errored'
  paused: boolean
  paused_at?: string
  pause_reason?: string
  last_action_at?: string
  budget_usd_spent: number
  budget_tokens_spent: number
  budget_cycle_started_at: string
  action_count_24h: number
  session_count_active: number
  sessions_today: number
  display_name?: string
  role_title?: string
  avatar_url?: string
  bio?: string
  personality?: string
}

export interface HubAutonomyAgentSession {
  id: string
  agent_id: string
  department: string
  started_at: string
  ended_at?: string
  status: 'running' | 'completed' | 'paused' | 'errored'
  action_count: number
  active_row_id?: string | null
  active_claimed_at?: string | null
  active_heartbeat_at?: string | null
}

export interface HubAutonomyAgentActivity {
  id: number
  agent_id: string
  action: string
  target_table?: string
  target_id?: string
  reviewer_id?: number
  reviewer_decision?: 'approve' | 'reject'
  created_at: string
}

export interface HubDeptEscalation {
  id: string
  row_id?: string
  title: string
  status: string
  priority: string
  department: string
  product: string
  safety?: string
  gate?: string
  assigned_agent?: string
  wip_session_id?: string
  updated_at: string
}

export const hubAutonomy = {
  agents: (params: { department?: string; include_paused?: boolean } = {}) => {
    const usp = new URLSearchParams()
    if (params.department) usp.set('department', params.department)
    if (params.include_paused === false) usp.set('include_paused', '0')
    const qs = usp.toString()
    return request<{ agents: HubAutonomyAgent[]; total: number }>(
      `/hub/agents${qs ? '?' + qs : ''}`
    )
  },
  agent: (id: string) =>
    request<HubAutonomyAgent>(`/hub/agents/${encodeURIComponent(id)}`),
  sessions: (id: string, limit = 50) =>
    request<{ sessions: HubAutonomyAgentSession[]; total: number }>(
      `/hub/agents/${encodeURIComponent(id)}/sessions?limit=${limit}`
    ),
  activity: (id: string, limit = 100) =>
    request<{ activity: HubAutonomyAgentActivity[]; total: number }>(
      `/hub/agents/${encodeURIComponent(id)}/activity?limit=${limit}`
    ),
  pause: (id: string, reason?: string) =>
    request<{ agent_id: string; paused: true }>(
      `/hub/agents/${encodeURIComponent(id)}/pause`,
      { method: 'POST', body: JSON.stringify({ reason: reason || '' }) }
    ),
  resume: (id: string) =>
    request<{ agent_id: string; paused: false }>(
      `/hub/agents/${encodeURIComponent(id)}/resume`,
      { method: 'POST' }
    ),
  escalations: (dept: string, limit = 100) =>
    request<{ department: string; escalations: HubDeptEscalation[]; total: number }>(
      `/hub/departments/${encodeURIComponent(dept)}/escalations?limit=${limit}`
    ),
}

export type HubAccountWorkflowRow = {
  id: string
  row_id?: string | null
  type: string
  department: string
  product: string
  status: string
  priority: string
  title: string
  origin?: string | null
  assigned_agent?: string | null
  handoff_from?: string | null
  handoff_to?: string | null
  created_at: string
  updated_at: string
}

export type HubAccountSummary = {
  account_id: string
  first_seen_at: string
  last_activity_at: string
  row_count: number
  departments_touched: string[]
}

export type HubAccountDetail = {
  account_id: string
  first_seen_at?: string | null
  last_activity_at?: string | null
  row_count: number
  departments_touched: string[]
  workflow_items: HubAccountWorkflowRow[]
}

export const hubAccounts = {
  list: () =>
    request<{ accounts: HubAccountSummary[]; count: number }>(`/hub/accounts`),
  get: (id: string) =>
    request<HubAccountDetail>(`/hub/accounts/${encodeURIComponent(id)}`),
}

export type HubCOODigest = {
  product: string
  agent_id: string
  week_tag: string
  title: string
  body: string
  written_at: string
  row_id?: string | null
}

export type HubCOOAggregates = {
  touchpoints_last_7d: number
  agent_spend_usd_total: number
  upcoming_milestones: number
  escalations_to_joe: number
}

export type HubCOOOverview = {
  digests: HubCOODigest[]
  aggregates: HubCOOAggregates
}

export type HubDecisionRow = {
  id: string
  row_id?: string | null
  type: string
  department: string
  product: string
  priority: string
  title: string
  assigned_agent?: string | null
  updated_at: string
  account_id?: string | null
}

export type HubDecisionsResponse = {
  decisions: HubDecisionRow[]
  total: number
  limit: number
}

export const hubCOO = {
  overview: () => request<HubCOOOverview>(`/hub/coo/overview`),
  decisionsNeeded: (limit = 3) =>
    request<HubDecisionsResponse>(`/hub/decisions-needed?limit=${limit}`),
}

export const hubSupport = {
  list: (status: 'open' | 'closed' | 'all' = 'open') =>
    request<SupportListResponse>(`/hub/support/inquiries?status=${status}`),
  get: (id: number) =>
    request<SupportDetailResponse>(`/hub/support/inquiries/${id}`),
  reply: (id: number, body: string) =>
    request<{ reply_id: number; sent_at: string }>(
      `/hub/support/inquiries/${id}/reply`,
      { method: 'POST', body: JSON.stringify({ body }) }
    ),
  close: (id: number) =>
    request<{ closed_at: string }>(
      `/hub/support/inquiries/${id}/close`,
      { method: 'POST' }
    ),
  reopen: (id: number) =>
    request<{ reopened_at: string }>(
      `/hub/support/inquiries/${id}/reopen`,
      { method: 'POST' }
    ),
}

// HUB-11-2: pricing tiers CRUD
export interface HubPricingTier {
  id: number
  slug: string
  name: string
  stripe_product_id: string | null
  stripe_price_monthly_id: string | null
  stripe_price_annual_id: string | null
  price_monthly_cents: number | null
  price_annual_cents: number | null
  per_label: string
  tagline: string
  bullets: string[]
  cta_label: string
  cta_href: string
  emphasis: boolean
  display_order: number
  active: boolean
  last_synced_at: string | null
  created_at: string
  updated_at: string
}

export interface HubPricingSyncResult {
  synced: number
  created: number
  updated: number
  skipped: number
  errors: string[]
}

export const hubPricing = {
  list: () => request<{ tiers: HubPricingTier[] }>('/hub/pricing/tiers'),
  get: (id: number) => request<HubPricingTier>(`/hub/pricing/tiers/${id}`),
  create: (body: Partial<HubPricingTier>) =>
    request<HubPricingTier>('/hub/pricing/tiers', {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  update: (id: number, body: Partial<HubPricingTier>) =>
    request<HubPricingTier>(`/hub/pricing/tiers/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    }),
  remove: (id: number) =>
    request<void>(`/hub/pricing/tiers/${id}`, { method: 'DELETE' }),
  sync: () =>
    request<HubPricingSyncResult>('/hub/pricing/sync', { method: 'POST' }),
}

// ── HUB-11-4: bulk subscriber migration ──────────────────────────────────────

export interface HubPricingSubscriberCompany {
  id: number
  name: string
  subscription_status: string
}

export interface HubPricingSubscriberPlan {
  plan: string
  count: number
  companies: HubPricingSubscriberCompany[]
}

export interface HubPricingMigrateResult {
  migrated: number
  from_plan: string
  to_plan: string
  dry_run: boolean
}

export const hubPricingSubscribers = {
  list: (plan?: string) =>
    request<{ plans: HubPricingSubscriberPlan[] }>(
      `/hub/pricing/subscribers${plan ? `?plan=${encodeURIComponent(plan)}` : ''}`
    ),
  migrate: (from_plan: string, to_plan: string, dry_run: boolean) =>
    request<HubPricingMigrateResult>('/hub/pricing/migrate-plan', {
      method: 'POST',
      body: JSON.stringify({ from_plan, to_plan, dry_run }),
    }),
}

// ── Marketing agent runs (MARKETING-AGENT-KILL-SWITCH) ───────────────────

export interface HubAgentRun {
  id: string
  task_type: string
  trigger: string
  status: 'pending' | 'running' | 'done' | 'failed'
  model: string
  input_tokens: number
  output_tokens: number
  cost_usd: number
  error?: string
  started_at?: string
  finished_at?: string
  created_at: string
}

export const hubAgentRuns = {
  list: (limit = 50) =>
    request<{ runs: HubAgentRun[]; total: number }>(
      `/hub/agent-runs?limit=${limit}`
    ),
  status: () =>
    request<{ paused: boolean }>('/hub/agent-runs/status'),
  pause: () =>
    request<{ paused: true }>('/hub/agent-runs/pause', { method: 'POST' }),
  resume: () =>
    request<{ paused: false }>('/hub/agent-runs/resume', { method: 'POST' }),
}

// ── Hub analytics ────────────────────────────────────────────────────────────

export interface HubFunnelStep {
  label: string
  value: number
  max: number
}

export interface HubNPSResult {
  score: number | null
  responses: number
  note?: string
}

export const hubAnalytics = {
  funnel: () => request<HubFunnelStep[]>('/hub/analytics/funnel'),
  nps: () => request<HubNPSResult>('/hub/analytics/nps'),
}

// ── Integration health ───────────────────────────────────────────────────────

export interface IntegrationStatus {
  key: string
  label: string
  note: string
  tone: 'ok' | 'warn' | 'err'
  mode?: string
}

export interface IntegrationHealthResponse {
  integrations: IntegrationStatus[]
}

export const hubIntegrations = {
  health: () => request<IntegrationHealthResponse>('/hub/integration-health'),
}

// ── Hub triggers — AUTONOMY-HUB-ENGINEERING-RELEASE-BUTTONS ──────────────────

export interface WorkflowRunSummary {
  id: number
  status: string
  conclusion: string | null
  name: string
  created_at: string
  html_url: string
}

export interface TriggerStatus {
  desktop: { runs: WorkflowRunSummary[]; error: string | null }
  ios: { runs: WorkflowRunSummary[]; error: string | null }
}

export interface SpawnRequest {
  id: number
  requested_at: string
  fired_at: string | null
  canceled_at: string | null
}

export const hubTriggers = {
  status: () => request<TriggerStatus>('/hub/triggers/status'),
  desktopRelease: () =>
    request<{ queued: boolean; workflow: string }>(
      '/hub/triggers/desktop-release', { method: 'POST' }
    ),
  iosRelease: () =>
    request<{ queued: boolean; workflow: string }>(
      '/hub/triggers/ios-release', { method: 'POST' }
    ),
}

export const hubSpawnRequest = {
  post: () => request<{ id: number; status: string }>('/hub/spawn-request', { method: 'POST' }),
  list: () => request<{ requests: SpawnRequest[] }>('/hub/spawn-request'),
}

// ── Email template composer ──────────────────────────────────────────────────

export interface HubEmailTemplateMeta {
  name: string
  dir: string
  label: string
  audience: string
  has_override: boolean
}

export interface HubEmailTemplateDetail extends HubEmailTemplateMeta {
  default_html: string
  override_html: string | null
  override_at: string | null
  stubs: Record<string, string>
}

export interface HubEmailTemplateLogEntry {
  id: number
  template_name: string
  action: 'set' | 'reset'
  admin_id: number | null
  admin_name: string | null
  created_at: string
}

export const hubEmailTemplates = {
  list: () =>
    request<{ items: HubEmailTemplateMeta[] }>('/hub/email-templates'),
  get: (name: string) =>
    request<HubEmailTemplateDetail>(`/hub/email-templates/${encodeURIComponent(name)}`),
  put: (name: string, htmlBody: string) =>
    request<{ ok: boolean }>(`/hub/email-templates/${encodeURIComponent(name)}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ html_body: htmlBody }),
    }),
  delete: (name: string) =>
    request<{ ok: boolean }>(`/hub/email-templates/${encodeURIComponent(name)}`, {
      method: 'DELETE',
    }),
  preview: (name: string, htmlBody: string) =>
    request<{ html: string }>(`/hub/email-templates/${encodeURIComponent(name)}/preview`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ html_body: htmlBody }),
    }),
  log: (name: string) =>
    request<{ items: HubEmailTemplateLogEntry[] }>(`/hub/email-templates/${encodeURIComponent(name)}/log`),
}

// ── GitHub Issues cache — TENON-MIGRATION-2 ─────────────────────────────────

export interface GithubIssueItem {
  issue_number: number
  repo: string
  title: string
  body_summary: string
  body: string
  labels: Array<{ name: string }>
  state: string
  assignee: string
  github_url: string
  status_label: string
  priority_label: string
  type_label: string
  project_label: string
  safety_label: string
  /** Parsed from ## Gate Condition section; non-empty when safety is yellow/blocked */
  gate: string
  /** haiku | sonnet | opus | '' */
  model_hint: string
  /** SHA of the PR merge commit (set when spawn ships via PR) */
  merged_commit_sha: string
  /** Set when PR merges (status flips to done) */
  shipped_at: string | null
  /** Set when /mark-deployed fires after CI deploy */
  deployed_at: string | null
  /** Updated on every issue webhook event (spawn claim comments, label changes) */
  last_heartbeat_at: string | null
  issue_created_at: string
  issue_updated_at: string
  last_synced_at: string
}

export interface GithubIssuesListResponse {
  items: GithubIssueItem[]
  total: number
  repo: string
}

export interface SiwaEvent {
  ts: string
  branch: string
  message: string
  is_native: boolean
  remote_addr?: string
}

export interface SiwaTraceResponse {
  events: SiwaEvent[]
}

export const hubSiwaTrace = (since?: string): Promise<SiwaTraceResponse> => {
  const qs = since ? `?since=${encodeURIComponent(since)}` : ''
  return request<SiwaTraceResponse>(`/hub/siwa-trace${qs}`)
}

export const hubGithubIssues = {
  list: (params: { status?: string; priority?: string; project?: string; state?: string } = {}) => {
    const usp = new URLSearchParams()
    if (params.status) usp.set('status', params.status)
    if (params.priority) usp.set('priority', params.priority)
    if (params.project) usp.set('project', params.project)
    if (params.state) usp.set('state', params.state)
    const qs = usp.toString()
    return request<GithubIssuesListResponse>(`/hub/github-issues${qs ? '?' + qs : ''}`)
  },
  get: (issueNumber: number) =>
    request<GithubIssueItem>(`/hub/github-issues/${issueNumber}`),
  sync: () => request<{ status: string }>('/hub/github-issues/sync', { method: 'POST' }),
}

// ── Mission Control (OBJECTIVE-0 #1224 / #1226) ──────────────────────────────
//
// Read-only aggregation endpoints for /mission-control. Contract:
// docs/features/mission-control.md. Backed by tenon_github_issues_cache +
// agent_audit_log/agent_sessions + error_events on the hub mux.

export interface HubMissionWaitingItem {
  issue_number: number
  title: string
  github_url: string
  labels: string[]
  age_days: number
  /** whole seconds since issue_created_at — humanized client-side (Xm/Xh/Xd) so
   *  sub-day ages don't read "0d". age_days kept for back-compat. */
  age_seconds: number
  /** narrow waiting-on-joe kinds: operator (OPERATOR-ACTION title), decision
   *  (needs:joe label), needs-human (Cass-parked question). Alarm items reuse
   *  this shape with kind "alarm" (never rendered via the KIND maps). */
  kind: 'operator' | 'decision' | 'needs-human'
  /** "p0".."p3" from the priority: label, "" when unlabeled */
  priority: string
}

export interface HubMissionWaitingResponse {
  items: HubMissionWaitingItem[]
  total: number
}

export interface HubMissionObjectiveProgress {
  /** open issues carrying this objective's `objective:N` label AND status:queued */
  queued: number
  /** issues carrying this objective's `objective:N` label closed in the last 7 days */
  shipped_7d: number
  /** open issues carrying this objective's `objective:N` label AND safety:blocked */
  blocked: number
}

export interface HubMissionObjective {
  issue_number: number
  title: string
  github_url: string
  /** true when the title starts with "OBJECTIVE-0" (the framing objective) */
  is_frame: boolean
  /** real progress scoped to this objective's `objective:N` label (HUB-OBJECTIVE-TAGGING, #1249) —
   *  all zero until the label has been applied to issues. */
  progress: HubMissionObjectiveProgress
}

export interface HubMissionQueueSummary {
  queued_total: number
  by_priority: { p0: number; p1: number; p2: number; p3: number }
  oldest_queued_days: number
  /** whole seconds since the oldest queued issue was created — humanized client-side. */
  oldest_queued_secs: number
}

export interface HubMissionObjectivesResponse {
  objectives: HubMissionObjective[]
  queue: HubMissionQueueSummary
  shipped_7d: number
}

export interface HubMissionInboxItem {
  issue_number: number
  title: string
  github_url: string
  age_days: number
  /** whole seconds since issue_created_at — humanized client-side. */
  age_seconds: number
  /** has label origin:objective-planner */
  origin_planner: boolean
  /** "p0".."p3" from the priority: label, "" when unlabeled */
  priority: string
}

export interface HubMissionInboxResponse {
  items: HubMissionInboxItem[]
  total: number
}

export interface HubMissionFleetItem {
  agent_id: string
  last_action: string
  last_action_at: string
  last_heartbeat_at: string
  // "idle" = event-driven agent quiet beyond the alive window; it is never
  // "dark" (going quiet is expected for event-driven identities).
  status: 'alive' | 'quiet' | 'dark' | 'idle'
  // Coarse rhythm hint from the server identity map ("daily", "every 2h",
  // "Mon 06:30", …) or "event-driven" for agents outside the map.
  cadence?: string
  session_count_7d: number
}

/** Feature doc says "named arrays for objectives/fleet"; tolerate either
 *  `agents` (named) or `items` (generic) so the panel survives both. */
export interface HubMissionFleetResponse {
  agents?: HubMissionFleetItem[]
  items?: HubMissionFleetItem[]
  total?: number
}

export interface HubMissionIncidentError {
  error_code: number
  endpoint: string
  message: string
  count_48h: number
  last_seen: string
}

export interface HubMissionIncidentsResponse {
  alarm_issues: HubMissionWaitingItem[]
  errors: HubMissionIncidentError[]
  prod_deployed_at: string | null
}

// Per-agent drawer (#1258). GET /hub/mission/agent/{agent_id}.

export interface HubMissionAgentIdentity {
  agent_id: string
  display_name: string
  role: string
  cadence: string
}

export interface HubMissionAgentTickSummary {
  summary: string
  created_at: string
}

export interface HubMissionAgentAction {
  action: string
  target_table: string | null
  target_id: string | null
  payload: any
  created_at: string
}

export interface HubMissionAgentSession {
  started_at: string
  ended_at: string | null
  status: string
}

export interface HubMissionAgentResponse {
  identity: HubMissionAgentIdentity
  latest_tick_summary: HubMissionAgentTickSummary | null
  recent_actions: HubMissionAgentAction[]
  sessions: HubMissionAgentSession[]
}

// COO Overview panel (#1250). GET /hub/mission/coo-overview.

export interface HubMissionCOOFeatureAdoptionItem {
  event: string
  distinct_users_7d: number
}

/** available=false means the widget genuinely has no data yet (PostHog not
 *  configured, or a query with no results) — `note` explains why. Never
 *  fabricated/zeroed to look measured. */
export interface HubMissionCOOFeatureAdoption {
  available: boolean
  note?: string
  items?: HubMissionCOOFeatureAdoptionItem[]
}

export interface HubMissionCOOPendingWidget {
  available: boolean
  note: string
}

export interface HubMissionCOOAgentActivity {
  sessions_7d: number
  actions_7d: number
}

export interface HubMissionCOOOverviewResponse {
  tenants: { total: number }
  weekly_active_users: number
  feature_adoption: HubMissionCOOFeatureAdoption
  agent_activity: HubMissionCOOAgentActivity
  revenue: HubMissionCOOPendingWidget
  cost: HubMissionCOOPendingWidget
}

// CS Support Metrics panel (#2111). GET /hub/mission/cs-metrics.

export interface HubMissionCSCategoryItem {
  category: string
  count: number
}

export interface HubMissionCSTrendPoint {
  date: string
  count: number
  /** tickets that day where reply_sent_status='sent' — same definition as
   *  HubMissionCSDeflection.rate, per-day instead of window-total, so the
   *  panel can plot deflection over time (not just the 30d snapshot). */
  deflected: number
}

/** rate is true zero-human-touch deflection — tickets where a Sage-drafted
 *  reply was actually sent (reply_sent_status='sent', CS-EMAIL-SEND #2753)
 *  via Joe's /send accept, divided by total tickets in the window. See
 *  docs/cs/support-strategy.md §7 for the full definition and why. */
export interface HubMissionCSDeflection {
  rate: number
  deflected: number
  total: number
  note: string
}

/** available=false when no ticket resolved in the window yet — never a
 *  fabricated zero. */
export interface HubMissionCSLatency {
  available: boolean
  note?: string
  avg_hours?: number
  median_hours?: number
  sample_size?: number
}

export interface HubMissionCSMetricsResponse {
  window_days: number
  total: number
  deflection: HubMissionCSDeflection
  response_latency: HubMissionCSLatency
  categories: HubMissionCSCategoryItem[]
  volume_trend: HubMissionCSTrendPoint[]
}

// ── Release Freshness (RELEASE-FRESHNESS-MC) ─────────────────────────────────
// GET /hub/mission/release-freshness — one row per published channel (web,
// desktop, iOS/Android store tracks) with how many merged commits touch that
// channel's paths since its published version was cut. commits_behind is null
// (never a fake number) when the count is unavailable; unreleased channels
// carry status:"unreleased" and no count. Contract: hub_release_freshness.go.

export type HubReleaseStaleness =
  | 'fresh' | 'aging' | 'stale' | 'critical' | 'unreleased' | 'unknown'

export interface HubReleaseChannel {
  id: string
  name: string
  platform: 'web' | 'desktop' | 'ios' | 'android' | string
  track: string
  published_version: string
  published_at: string
  /** merged commits touching this channel's paths since it published; null when
   *  unreleased or the count couldn't be fetched (frontend shows "—"). */
  commits_behind: number | null
  staleness: HubReleaseStaleness
  status: 'live' | 'unreleased' | string
  /** degradation / "100+" caveat, when present */
  note?: string
}

export interface HubReleaseFreshnessResponse {
  channels: HubReleaseChannel[]
  total: number
}

export const hubMission = {
  waitingOnJoe: () =>
    request<HubMissionWaitingResponse>('/hub/mission/waiting-on-joe'),
  releaseFreshness: () =>
    request<HubReleaseFreshnessResponse>('/hub/mission/release-freshness'),
  objectives: () =>
    request<HubMissionObjectivesResponse>('/hub/mission/objectives'),
  inbox: () =>
    request<HubMissionInboxResponse>('/hub/mission/inbox'),
  fleet: () =>
    request<HubMissionFleetResponse>('/hub/mission/fleet'),
  incidents: () =>
    request<HubMissionIncidentsResponse>('/hub/mission/incidents'),
  cooOverview: () =>
    request<HubMissionCOOOverviewResponse>('/hub/mission/coo-overview'),
  csMetrics: () =>
    request<HubMissionCSMetricsResponse>('/hub/mission/cs-metrics'),
  /** Clear a waiting-on-Joe gate: approve, or answer with a comment. Posts a
   *  GitHub comment and removes the gate labels server-side. */
  clearGate: (number: number, action: 'approve' | 'answer', text?: string) =>
    request<{ ok: boolean; labels_removed: string[] }>(
      `/hub/mission/waiting-on-joe/${number}/clear`,
      { method: 'POST', body: JSON.stringify({ action, text: text ?? '' }) }
    ),
  agent: (agentId: string) =>
    request<HubMissionAgentResponse>(`/hub/mission/agent/${encodeURIComponent(agentId)}`),
}

// Pipeline flow board + autonomy kill switch (#1248).

/** One drilldown row for a pipeline stage (#1340). Issue-backed stages set
 *  issue_number; the in-review stage is PR-backed and sets pr_number instead. */
export interface HubPipelineItem {
  issue_number?: number
  pr_number?: number
  title: string
  github_url: string
  age_in_stage_days: number
  /** whole seconds since the item entered the stage — humanized client-side
   *  (Xm/Xh/Xd). age_in_stage_days kept for back-compat. */
  age_in_stage_secs: number
  /** true when this item alone exceeds the stage's stuck threshold */
  stuck: boolean
}

export interface HubMissionPipelineStage {
  key: string
  label: string
  /** null when the stage source is unavailable (e.g. in-review with no GITHUB_TOKEN) */
  count: number | null
  /** age of the oldest item in the stage, in seconds; null when empty/unavailable */
  oldest_age_secs: number | null
  stuck_threshold_secs: number
  stuck: boolean
  source: 'cache' | 'github' | 'unavailable'
  measures: string
  /** honest caveat rendered as a subtle note (e.g. "review-overlap unknown" when
   *  the coding stage couldn't subtract the in-review set). Omitted when clean. */
  note?: string
  /** drilldown rows (#1340): the stage's items, oldest-first, capped at 25.
   *  Empty for a degraded stage (source unavailable) — the drawer shows the
   *  caveat instead of a list. */
  items?: HubPipelineItem[]
}

export interface HubMissionPipelineResponse {
  stages: HubMissionPipelineStage[]
  /** WAITING ON YOU side pool (#1341) — open status:waiting-joe issues. Not a
   *  flow stage; the one tile that means Joe himself is the bottleneck. */
  waiting_on_you?: HubMissionPipelineStage
  /** Honesty stamp (#1423) for every "cache"-sourced stage: MAX(last_synced_at)
   *  across tenon_github_issues_cache. Distinct from "refreshed Xs ago" (the
   *  client's own poll cadence) — the cache fills from webhooks plus a 30-min
   *  reconciliation cron, so this can trail "refreshed" by up to that window.
   *  null if the DB read failed. */
  cache_synced_at: string | null
  /** issue number -> linked open review PR number (#1380), for issues whose
   *  status:in-progress has an open needs-cass-review / needs-coder-followup
   *  PR already. Absence does NOT mean "still coding" unless review_overlap_ok
   *  is true — see that field. */
  issue_review_prs?: Record<string, number>
  /** false when the live GitHub review-PR linkage is incomplete (search
   *  failure) — an issue missing from issue_review_prs can't be trusted as
   *  "still coding" in that case; render it as stage-unknown instead. */
  review_overlap_ok?: boolean
}

export interface HubMissionAutonomyState {
  paused: boolean
  reason: string
  updated_at: string | null
}

// ── Release notes review (RELEASE-NOTES-AUTO #1254) ──────────────────────────
// Contract: docs/features/release-notes.md. Agent-generated changelog drafts
// (published_at IS NULL) awaiting Joe's approve/reject.

export interface HubPendingReleaseNote {
  id: number
  version: string
  title: string
  summary: string
  category: 'New Feature' | 'Improvement' | 'Bug Fix'
  is_major: boolean
  source_issue_numbers: number[]
  created_at: string
}

export interface HubReleaseNotesPendingResponse {
  items: HubPendingReleaseNote[]
  total: number
}

export const hubReleaseNotes = {
  pending: () =>
    request<HubReleaseNotesPendingResponse>('/hub/mission/release-notes/pending'),
  publish: (id: number) =>
    request<{ status: string }>(`/hub/mission/release-notes/${id}/publish`, { method: 'POST' }),
  reject: (id: number) =>
    request<{ status: string }>(`/hub/mission/release-notes/${id}/reject`, { method: 'POST' }),
}

// ── Marketing Approvals (MARKETING-MC-APPROVE #1578) ──────────────────────────
// Contract: docs/features/mkt-preview-approve.md §7. Open project:marketing +
// needs-human PRs awaiting Joe's visual review, sourced live from GitHub
// Search (PRs aren't in tenon_github_issues_cache).

export interface HubMissionMarketingPRItem {
  pr_number: number
  title: string
  github_url: string
  age_days: number
  age_seconds: number
}

export interface HubMissionMarketingPendingResponse {
  items: HubMissionMarketingPRItem[]
  total: number
  /** false when GITHUB_TOKEN is unset or the search failed — items is empty,
   *  not necessarily "nothing pending". */
  available: boolean
}

export const hubMissionMarketing = {
  pending: () =>
    request<HubMissionMarketingPendingResponse>('/hub/mission/marketing-pending'),
  /** Posts an approval comment and clears needs-human + safety:blocked (the
   *  label auto-merger.sh actually honors); ships on the next green-CI tick —
   *  this does not merge directly. */
  approve: (prNumber: number) =>
    request<{ ok: boolean; labels_removed: string[] }>(
      `/hub/mission/marketing-pending/${prNumber}/approve`,
      { method: 'POST' }
    ),
  /** Requires a non-empty note. Files a status:queued/project:marketing
   *  followup issue carrying the note, comments + closes the PR. */
  reject: (prNumber: number, note: string) =>
    request<{ ok: boolean; followup_issue: number; followup_url: string }>(
      `/hub/mission/marketing-pending/${prNumber}/reject`,
      { method: 'POST', body: JSON.stringify({ note }) }
    ),
}

export const hubMissionPipeline = () =>
  request<HubMissionPipelineResponse>('/hub/mission/pipeline')

export const hubMissionAutonomy = {
  get: () => request<HubMissionAutonomyState>('/hub/mission/autonomy'),
  /** platformAuth only — flips the fleet pause flag. */
  set: (paused: boolean, reason: string) =>
    request<HubMissionAutonomyState>('/hub/mission/autonomy', {
      method: 'POST',
      body: JSON.stringify({ paused, reason }),
    }),
}

